import { IReviewStepData } from 'dromo-uploader-js'
import { IDeveloperField } from 'dromo-uploader-react'
import {
  IPublicConnectionMethods,
  IFieldMetadata,
  IPositionSpec,
} from 'dromo-uploader-js/dist/interfaces'
import {
  siteActionSchema,
  addressSchema,
  nameSchema,
  citySchema,
  stateSchema,
  zipCodeSchema,
  latSchema,
  longSchema,
  notesSchema,
} from './commonFieldSchemas'

export interface ColumnSchemas {
  siteActionSchema: IDeveloperField
}

export async function addColumnsIfNeeded(
  instance: IPublicConnectionMethods,
  previewData: IReviewStepData,
  additionalRequiredFields: {
    schema: IDeveloperField
    position: IPositionSpec
  }[]
) {
  const headers = Object.values(previewData.headerMapping)
  // in manual input mode, there is no header mapping
  const isManualInput = headers.length === 0
  if (isManualInput) {
    await reorderManualInputFields(instance, previewData)
    return
  }

  const firstField = getFirstHeaderKey(previewData.fields)
  await instance.addField(siteActionSchema, {
    before: firstField,
  })

  if (!headers.includes(nameSchema.key)) {
    await instance.addField(nameSchema, { after: siteActionSchema.key })
  }
  if (!headers.includes(citySchema.key)) {
    await instance.addField(citySchema, { after: 'address' }) // address is required
  }
  if (!headers.includes(stateSchema.key)) {
    await instance.addField(stateSchema, { after: citySchema.key })
  }
  if (!headers.includes(zipCodeSchema.key)) {
    await instance.addField(zipCodeSchema, { after: stateSchema.key })
  }
  // We only need to check one because of the validation logic that requires both or none
  if (!headers.includes(latSchema.key)) {
    await instance.addField(latSchema, { after: zipCodeSchema.key })
    await instance.addField(longSchema, { after: latSchema.key })
  }
  if (!headers.includes(notesSchema.key)) {
    await instance.addField(notesSchema, { after: longSchema.key })
  }
  // add any additional required fields
  for (const additionalField of additionalRequiredFields) {
    if (headers.includes(additionalField.schema.key)) {
      continue
    }
    await instance.addField(additionalField.schema, additionalField.position)
  }
}

async function reorderManualInputFields(
  instance: IPublicConnectionMethods,
  previewData: IReviewStepData
): Promise<void> {
  const firstFieldKey = Object.keys(previewData.fields)[0]
  // In manual input mode, all fields are added in alphabetical order.
  // Reorder so that the most important fields are first
  await instance.removeField(siteActionSchema.key)
  await instance.removeField(nameSchema.key)
  await instance.removeField(addressSchema.key)
  await instance.removeField(citySchema.key)
  await instance.removeField(stateSchema.key)
  await instance.removeField(zipCodeSchema.key)
  await instance.removeField(latSchema.key)
  await instance.removeField(longSchema.key)
  await instance.removeField(notesSchema.key)

  await instance.addField(siteActionSchema, { before: firstFieldKey })
  await instance.addField(nameSchema, { after: siteActionSchema.key })
  await instance.addField(addressSchema, { after: nameSchema.key })
  await instance.addField(citySchema, { after: addressSchema.key })
  await instance.addField(stateSchema, { after: citySchema.key })
  await instance.addField(zipCodeSchema, { after: stateSchema.key })
  await instance.addField(latSchema, { after: zipCodeSchema.key })
  await instance.addField(longSchema, { after: latSchema.key })
  await instance.addField(notesSchema, { after: longSchema.key })
}

/** Find the key of the first column that will be shown in the Review tab */
function getFirstHeaderKey(fields: IFieldMetadata): string {
  const header = Object.keys(fields).reduce(
    (currMinKey: string | null, currKey) => {
      const currHeaderInd = fields[currKey].fileHeaderIndex

      const currMinHeaderInd = currMinKey
        ? fields[currMinKey].fileHeaderIndex
        : null

      if (
        currHeaderInd !== null &&
        (currMinHeaderInd === null || currHeaderInd < currMinHeaderInd)
      ) {
        return currKey
      }

      return currMinKey
    },

    null
  )

  if (!header) {
    throw new Error('Could not find first column header')
  }

  return header
}
